import type { SortColumn } from "react-data-grid";
import { Column } from "react-data-grid";
import { DateTime } from "luxon";
import {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useMemo
} from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Label, SelectPayment } from "./SelectPayment";
import { useNavigate } from "react-router-dom";
import { AccountStatusBadge } from "./AccountStatusBadge";
import {
  AccountStatusVariant,
  PaymentStatuses,
  getEmptyState
} from "../constants";
import { Renter, SelectValue, Verification } from "../types";
import { useAuthContext } from "providers/Auth";
import { RvStatusBadge } from "./RvStatusBadge";
import { resolveDisplayAccountStatus, toVerificationPayload } from "../utils";
import { formatDate } from "views/rent-reporting-id/slices/SubscriptionInfo/slices/RentReportingTable/utils";
import { DataGridTable } from "shared/components/DataGridTable";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { useLayout } from "providers/Layout/hooks";
import { CheckCircleBroken } from "untitledui-js-base";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import styles from "./SubscribersTable.module.css";

type Props = {
  rentersData: Renter[];
  hasSearchParams?: boolean;
  selectDefaultValue: PaymentStatuses;
  selectValues: SelectValue[];
  setSelectValues: Dispatch<SetStateAction<SelectValue[]>>;
  isLoading: boolean;
  sorted: { key: string; desc: boolean };
  setSorted: (sorted: { key: string; desc: boolean }) => void;
  activeTab: AccountStatusVariant;
  isReportingSingleRenter: boolean;
  reportSingleRenter: (data: {
    subscriberId: number;
    verificationId: number;
    verifications: Verification;
  }) => void;
  pagination?: React.ReactNode;
  perPageRows?: number;
};

export const SubscribersTable: FC<Props> = ({
  hasSearchParams,
  rentersData,
  selectDefaultValue,
  selectValues,
  setSelectValues,
  isLoading,
  sorted,
  setSorted,
  activeTab,
  reportSingleRenter,
  isReportingSingleRenter,
  pagination,
  perPageRows
}) => {
  const navigate = useNavigate();
  const { type, permissions } = useAuthContext();
  const { sectionHeight } = useLayout();

  const emptyState = getEmptyState(activeTab, type.isSDK);
  const noRowsFallback = (
    <NoRowsFallback
      title={hasSearchParams ? "No results found" : emptyState.title}
      text={hasSearchParams ? null : emptyState.subtitle}
    />
  );

  const sortColumns: SortColumn[] = useMemo(
    () => [
      {
        columnKey: sorted.key || "id",
        direction: sorted.desc ? "DESC" : "ASC"
      }
    ],
    [sorted.key, sorted.desc]
  );

  const handleSortColumnsChange = (newSortColumns: SortColumn[]) => {
    const lastSort = newSortColumns[newSortColumns.length - 1];
    // Grid can call with empty array when sort is cleared
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime empty sort
    if (lastSort)
      setSorted({
        key: lastSort.columnKey,
        desc: lastSort.direction === "DESC"
      });
    else setSorted({ key: "id", desc: false });
  };

  const handleChange = (id: number, newValue: string | number) => {
    setSelectValues((prev: SelectValue[]) => {
      const existingItemIndex = prev.findIndex((item) => item.id === id);
      if (newValue === selectDefaultValue)
        return prev.filter((item) => item.id !== id);
      else if (existingItemIndex !== -1)
        return prev.map((item, index) =>
          index === existingItemIndex ? { ...item, action: newValue } : item
        );
      else return [...prev, { id, action: newValue }];
    });
  };

  const handleReportClick = (renter: Renter) => {
    const selectedRenter = selectValues.find(
      (r) => r.id === renter.verification_id
    );
    const verifications = selectedRenter
      ? toVerificationPayload(selectedRenter.action)
      : { action: selectDefaultValue, months_delinquency: undefined };
    reportSingleRenter({
      subscriberId: renter.id,
      verificationId: renter.verification_id,
      verifications
    });
    setSelectValues([]);
  };

  const columns = useMemo((): Column<Renter>[] => {
    const enrollmentColumns: Column<Renter>[] = [
      {
        key: "status",
        name: "Enrollment status",
        minWidth: 165,
        resizable: true,
        renderCell: ({ row }) => (
          <div className="min-w-0 flex items-center">
            <AccountStatusBadge
              status={resolveDisplayAccountStatus(row, type)}
            />
          </div>
        )
      },
      {
        key: "enrollment_dates",
        name: "Enrollment",
        minWidth: 165,
        resizable: true,
        renderHeaderCell: () => (
          <Tooltip delay={{ open: 100, close: 0 }}>
            <TooltipTrigger asChild>
              <span>Enrollment</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[14]">
              Displays the enrollment creation date and the date it was
              approved. A checkmark indicates active status.
            </TooltipContent>
          </Tooltip>
        ),
        renderCell: ({ row }) => {
          const created = row.active_enrollment?.created_at;
          const approved = row.active_enrollment?.approved_at;
          const createdFormatted = formatDate(created, "MMM DD, YYYY", "--");
          const approvedFormatted = formatDate(approved, "MMM DD, YYYY", "--");
          const createdWithTime = formatDate(
            created,
            "MMM DD, YYYY [at] h:mm A",
            "--"
          );
          const approvedWithTime = formatDate(
            approved,
            "MMM DD, YYYY [at] h:mm A",
            "--"
          );
          return (
            <Tooltip placement="bottom-start" delay={{ open: 250, close: 0 }}>
              <TooltipTrigger asChild>
                <div className="min-w-0 flex flex-col text-sm font-medium text-content-1000 h-full justify-center gap-0.5">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                    {createdFormatted}
                  </span>
                  <span className="flex items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                    {approved ? (
                      <CheckCircleBroken
                        size="16"
                        className="shrink-0 stroke-success-700"
                      />
                    ) : null}
                    {approvedFormatted}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[14]">
                Created: {createdWithTime}.
                <br />
                Approved: {approvedWithTime}.
              </TooltipContent>
            </Tooltip>
          );
        }
      },
      {
        key: "lease_period",
        name: "Lease period",
        minWidth: 165,
        resizable: true,
        renderHeaderCell: () => (
          <Tooltip delay={{ open: 100, close: 0 }}>
            <TooltipTrigger asChild>
              <span>Lease period</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[14]">
              The start and end dates of the resident&apos;s lease.
            </TooltipContent>
          </Tooltip>
        ),
        renderCell: ({ row }) => {
          const leaseStart = row.lease_start_date || row.move_in_date;
          const leaseEnd = row.lease_end_date;
          const startDate = formatDate(leaseStart, "MMM DD, YYYY", "--");
          const endDate = formatDate(leaseEnd, "MMM DD, YYYY", "--");
          return (
            <Tooltip placement="bottom-start" delay={{ open: 250, close: 0 }}>
              <TooltipTrigger asChild>
                <div className="min-w-0 flex flex-col text-sm font-medium text-content-1000 h-full justify-center">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                    {startDate}
                  </span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                    {endDate}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[14]">
                Lease start: {startDate}.
                <br />
                Lease end: {endDate}.
              </TooltipContent>
            </Tooltip>
          );
        }
      }
    ];

    const baseColumns: Column<Renter>[] = [
      {
        key: "id",
        name: "Renter",
        minWidth: 310,
        sortable: true,
        headerCellClass: styles.headerSortIconAfterText,
        renderCell: ({ row }) => (
          <div className="min-w-0 flex flex-col gap-1 justify-center">
            <p className="text-content-1000 font-medium text-sm truncate">
              {row.name}
            </p>
            <p className="text-content-400 text-xs font-normal truncate">
              {row.address}
            </p>
          </div>
        )
      }
    ];

    if (type.isSDK)
      return [
        ...baseColumns,
        {
          key: "email",
          name: "Email",
          minWidth: 200,
          resizable: true,
          renderCell: ({ row }) => (
            <span className="text-sm text-content-500 block overflow-hidden text-ellipsis whitespace-nowrap">
              {row.email || "--"}
            </span>
          )
        },
        ...enrollmentColumns
      ];

    const reportingMonthName = DateTime.now()
      .minus({ months: 1 })
      .toFormat("MMMM");

    return [
      ...baseColumns,
      ...enrollmentColumns,
      {
        key: "reporting_status",
        name: `${reportingMonthName} status`,
        minWidth: 200,
        resizable: true,
        renderCell: ({ row }) => {
          const id = row.verification_id;
          const delinquencyOptions = row.available_delinquency_options;
          const options = delinquencyOptions?.length
            ? delinquencyOptions.map((option) => ({
                value: option.months_delinquency,
                label: (
                  <Label
                    title={option.name}
                    icon={
                      <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                        <p className="text-danger-500 text-sm font-bold">
                          {option.months_delinquency}
                        </p>
                      </div>
                    }
                  />
                )
              }))
            : [];

          const isNeedToReport =
            resolveDisplayAccountStatus(row, type) ===
              AccountStatusVariant.NeedToReport &&
            permissions.boomReportWriteAccess &&
            !type.isSDK;

          let cellContent: ReactNode;
          if (isNeedToReport)
            cellContent = (
              <SelectPayment
                id={String(id)}
                value={
                  selectValues.find((item) => item.id === id)?.action ||
                  selectDefaultValue
                }
                handleChange={(e) => handleChange(id, e.target.value)}
                options={options}
              />
            );
          else if (row.rv_status)
            cellContent = <RvStatusBadge status={row.rv_status} />;
          else cellContent = "--";

          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              {cellContent}
            </div>
          );
        }
      },
      {
        key: "reporting_action",
        name: "",
        minWidth: 100,
        resizable: false,
        renderCell: ({ row }) => {
          const isNeedToReport =
            resolveDisplayAccountStatus(row, type) ===
              AccountStatusVariant.NeedToReport &&
            permissions.boomReportWriteAccess &&
            !type.isSDK;

          if (!isNeedToReport) return null;

          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.XS}
                loading={isReportingSingleRenter}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  !isReportingSingleRenter && handleReportClick(row);
                }}
              >
                Report
              </Button>
            </div>
          );
        }
      }
    ];
  }, [
    type.isSDK,
    type.isCombinedSearchV2,
    permissions.boomReportWriteAccess,
    selectDefaultValue,
    selectValues,
    isReportingSingleRenter
  ]);

  return (
    <div className="relative">
      <DataGridTable
        viewPortHeight={sectionHeight}
        columns={columns}
        rows={rentersData}
        rowHeight={76}
        headerRowHeight={44}
        loading={isLoading}
        onRowClick={(row) => navigate(`/rent-reporting/${row.id}`)}
        noRowsFallback={noRowsFallback}
        sortColumns={sortColumns}
        onSortColumnsChange={handleSortColumnsChange}
        footer={pagination}
        perPageRows={perPageRows}
        viewPortHeightOffset={150}
      />
    </div>
  );
};
